import { Avatar } from '@/components/Avatar';
import { Button } from '@/ui/Button';
import { cn } from '@/lib/cn';
import type { Celebracion } from './api';

/**
 * piezas.tsx — las partes que se repiten en Celebraciones.
 *
 * `TarjetaDeCelebracion` existe porque el vanilla la tenía copiada:
 * `renderBirthdays` y `renderAnniversaries` eran la misma función con dos
 * textos, y **una escapaba `avatar_url` y la otra no**.
 *
 * El buscador de personas también vivía aquí —el vanilla tenía **tres** copias,
 * con tres ids distintos y solo dos amortiguando bien—. El 2026-09-18 se mudó a
 * `components/BuscadorDePersonas`, porque Comunidades iba a necesitar una cuarta:
 * dos copias en React más la que pedía el módulo 9.
 */

/* ── El día como cabecera, no repetido en cada tarjeta ─────────────────── */

const cuando = (dias: number) =>
  dias === 0 ? 'Hoy' : dias === 1 ? 'Mañana' : `En ${dias} días`;

/** «Martes 1 de septiembre», calculado desde `days_until`.
 *
 *  No se le pide al servidor: `date_display` viene como "dd/mm" y añadir el día
 *  de la semana obligaría a tocar el endpoint. Sumar días a hoy da lo mismo sin
 *  cambiar el contrato. */
function fechaLarga(dias: number) {
  const d = new Date();
  d.setDate(d.getDate() + dias);
  const t = d.toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' });
  return t.charAt(0).toUpperCase() + t.slice(1);
}

export function CabeceraDeDia({ dias, cuantos }: { dias: number; cuantos: number }) {
  return (
    <h3 className="cel-dia-cab">
      <span className="cel-dia-cuando">{cuando(dias)}</span>
      <span className="cel-dia-fecha">{fechaLarga(dias)}</span>
      <span className="cel-dia-cuenta">
        {cuantos} {cuantos === 1 ? 'persona' : 'personas'}
      </span>
    </h3>
  );
}

/** Reparte las celebraciones por día, en orden.
 *
 *  El servidor ya las manda ordenadas por proximidad; aquí solo se agrupan.
 *  «En 3 días» repetido en ocho tarjetas seguidas es ocho veces el mismo dato;
 *  dicho una vez, encabeza. */
export function agrupaPorDia(items: Celebracion[]): Array<[number, Celebracion[]]> {
  const grupos = new Map<number, Celebracion[]>();
  for (const u of items) {
    if (!grupos.has(u.days_until)) grupos.set(u.days_until, []);
    grupos.get(u.days_until)!.push(u);
  }
  return [...grupos.entries()].sort((a, b) => a[0] - b[0]);
}

/* ── La tarjeta ────────────────────────────────────────────────────────── */

/** Una celebración.
 *
 *  ## El avatar deja de ser naranja
 *
 *  `.celebration-avatar` llevaba `background: var(--orange)` y un anillo de 3 px
 *  del mismo color, **en todas las tarjetas**. El filete naranja de «hoy» está
 *  ahí para decir dónde hay algo que hacer —el botón Felicitar— y el CSS lo
 *  defiende así con todas las letras; el problema era que competía contra sí
 *  mismo. Medido en el navegador el 2026-09-11 con tres tarjetas en pantalla:
 *  **seis elementos con naranja, y los tres avatares lo llevaban, fueran de hoy
 *  o no**. Un anillo de 64 px pesa más que un filete de 3.
 *
 *  Ahora el avatar usa el color que `Utils.avatarColor` deriva del nombre, que
 *  es el que esa misma persona ya tiene en el directorio, en el muro y en
 *  Inicio. El naranja se queda **solo** en el filete de «hoy», y ahí sí
 *  significa una cosa.
 */
export function TarjetaDeCelebracion({
  c, detalle, alFelicitar, felicitando,
}: {
  c: Celebracion;
  /** La línea del medio: «Cumpleaños 11/09» o «5 años en la empresa». */
  detalle?: React.ReactNode;
  alFelicitar?: (userId: number) => void;
  felicitando?: boolean;
}) {
  const esHoy = c.days_until === 0;
  return (
    <div className={cn('celebration-card', esHoy && 'today')}>
      {/* `foto` va como prop, no interpolada en un `src`. En el vanilla una de
          las dos tarjetas escapaba `avatar_url` y la otra no — el defecto y su
          arreglo convivían en el mismo archivo, a 240 líneas. */}
      <Avatar nombre={c.full_name} foto={c.avatar_url} tamano={64} className="text-5" />

      <div className="min-w-0 flex-1">
        <div className="celebration-name truncate">{c.full_name}</div>
        {detalle ?? (c.position && <div className="celebration-type">{c.position}</div>)}
      </div>

      {c.type === 'work_anniversary' && c.years != null ? (
        <span className="cel-anios">
          <b>{c.years}</b> {c.years === 1 ? 'año' : 'años'}
        </span>
      ) : c.type === 'birthday' ? (
        <span className="cel-tipo">
          <i className="bi bi-cake2" aria-hidden="true" /> Cumpleaños
        </span>
      ) : null}

      {esHoy && alFelicitar && (
        <Button size="sm" onClick={() => alFelicitar(c.user_id)} disabled={felicitando}>
          <i className="bi bi-send-fill" aria-hidden="true" />
          {felicitando ? 'Enviando…' : 'Felicitar'}
        </Button>
      )}
    </div>
  );
}

/* ── El buscador de persona, uno solo ──────────────────────────────────── */

